import {useState} from 'react';
import {useQuery} from 'react-query';
import toast from 'react-hot-toast';
import api from 'api';
import {
  CompanyNotification,
  NotificationType,
} from 'types/notification-settings';

// Default message templates
const DEFAULT_TEMPLATES = {
  customer_payment:
    'Hi {Company}! Customer [Customer Name] has successfully completed a payment of $[Amount] for order #[Order ID]. Please review the transaction details in your company dashboard to ensure proper processing and record-keeping.',
  new_customer:
    'Hi {Company}! A new customer, [Customer Name], has just registered. Contact details are [Customer Email] and [Customer Phone]. You can view their profile and manage their activity from your dashboard.',
  new_order:
    'Hi {Company}! Customer [Customer Name] has placed a new job (#[Job ID]) totaling $[Amount]. You can review the job details and start processing it from your company dashboard. Please ensure timely confirmation and fulfillment.',
  new_route:
    'A new delivery route (#[Route ID]) has been dispatched, and deliveries are now on their way to customers. Please ensure the assigned driver or team ([Driver/Team Name]) follows the schedule and updates delivery statuses promptly. You can track real-time progress in your dispatch dashboard.',
};

async function fetchNotifications(): Promise<CompanyNotification[]> {
  const {data} = await api.get<CompanyNotification[]>(
    '/companies/company-notifications/'
  );
  return data;
}

async function updateNotification(
  id: number,
  isEnabled: boolean
): Promise<void> {
  await api.put(`/companies/company-notifications/${id}/`, {
    is_enabled: isEnabled,
  });
}

function getNotificationTemplate(
  notifications: CompanyNotification[],
  type: NotificationType
): string {
  const emailNotif = notifications.find(
    (n) => n.notification_type === type && n.channel === 'email'
  );
  const smsNotif = notifications.find(
    (n) => n.notification_type === type && n.channel === 'sms'
  );

  return (
    emailNotif?.message_template ||
    smsNotif?.message_template ||
    DEFAULT_TEMPLATES[type]
  );
}

const useNotifications = () => {
  const [loadingStates, setLoadingStates] = useState<Record<number, boolean>>(
    {}
  );

  const {
    data: notifications,
    refetch,
    isLoading,
  } = useQuery('company-notifications', fetchNotifications, {
    onError: (error: any) => {
      toast.error('Failed to load notification settings');
    },
    refetchOnWindowFocus: true,
  });

  const handleToggleChange = async (
    notification: CompanyNotification,
    newValue: boolean
  ) => {
    try {
      setLoadingStates((prev) => ({...prev, [notification.id]: true}));
      await updateNotification(notification.id, newValue);
      toast.success('Notification setting updated');
      await refetch();
    } catch (error) {
      toast.error('Failed to update notification setting');
    } finally {
      setLoadingStates((prev) => ({...prev, [notification.id]: false}));
    }
  };

  const getNotification = (
    type: NotificationType,
    channel: 'email' | 'sms'
  ) => {
    return notifications?.find(
      (n) => n.notification_type === type && n.channel === channel
    );
  };

  const getTemplate = (type: NotificationType) => {
    if (!notifications) return DEFAULT_TEMPLATES[type];
    return getNotificationTemplate(notifications, type);
  };

  return {
    notifications,
    isLoading,
    loadingStates,
    handleToggleChange,
    getNotification,
    getTemplate,
  };
};

export default useNotifications;
